import type { OrgType } from "@shared/types/org";
import type { CategoryBreakdown } from "@shared/types/stageCategories";

import type { AcquisitionData } from "@/stores/campaignStore";

import apiClient from "./axios";

// ========================================
// TYPES
// ========================================

/**
 * Backend API response from /organizations/firm-statistics endpoint
 */
export interface FirmStatisticsResponse {
  campaignCount: number;
  campaigns: Array<{
    id: string;
    name: string;
  }>;
  cases: number;
  casesThisWeek: number;
  clients: number;
  contact?: string;
  email?: string;
  fallout: number;
  id: string;
  logoUrl: string;
  /** Display id + every canonical/alias that rolls up into this firm row. The
   *  firms list keys a grouped firm by its root-identity id, so a canonical/
   *  alias id never equals `id`; FirmDetails matches against this to resolve a
   *  member id back to the firm and redirect to its canonical page id. */
  memberOrgIds: string[];
  name: string;
  /** Cases in ON_HOLD stage. RAP-294 Phase 2 — used by the firms list and
   *  directory to display Open counts that exclude on-hold cases. */
  onHold: number;
  /** True Open count: non-excluded − fallout − onHold − settled. Phase 2
   *  contract that replaces the legacy `cases - fallout` proxy. */
  openCases: number;
  orgType: string;
  /** Cases in RESOLUTION_PAYOUT stage. Drives "Settled+Resolution" sort on Top Firms. */
  resolutionPayout: number;
  /** Cases in SETTLED stage. Drives "Settled" sort on Top Firms. */
  settled: number;
}

/**
 * Backend API response from /organizations/:firmId/campaign-statistics endpoint
 * Returns detailed campaign statistics including stage breakdown
 */
export interface FirmCampaignStatistics {
  /** Campaign.category (free-form like "Pharmaceutical"). Drives the Type
   *  column icon and sub-line on CampaignPerformanceTable. Null when the
   *  campaign has no category set. */
  campaignCategory: null | string;
  campaignId: string;
  campaignName: string;
  casesThisWeek: number;
  // Category rollup powering the Cases by Stage chart on Firm Details.
  categoryBreakdown: CategoryBreakdown;
  fallout: number;
  /** RAP-294 Phase 2 per-campaign split. Lets FirmDetails show true Open
   *  counts instead of approximating with `totalCases - fallout`. */
  onHoldCases: number;
  openCases: number;
  settledCases: number;
  totalCases: number;
}

/**
 * Trimmed user shape for the per-organization users endpoint
 * (`GET /admin/organizations/:orgId/users`). Backs FirmDetails and
 * OrgUsersModal. Surfaces only users the caller has granted access to
 * via caller-owned campaigns — see RAP-227.
 */
export interface OrganizationUser {
  avatarUrl: null | string;
  campaignCount: number;
  email: string;
  firstName: null | string;
  id: string;
  isActive: boolean;
  lastLoginAt: null | string;
  lastName: null | string;
  username: string;
}

export interface OrganizationUsersResponse {
  users: OrganizationUser[];
}

export interface FirmTopGrowthItem {
  casesThisMonth: number;
  casesThisWeek: number;
  category: null | string;
  id: string;
  name: string;
}

export interface FirmKpiStats {
  closedAtReview: number;
  closedDuringCase: number;
  fallout: number;
  falloutRate: number;
  // RAP-294 Phase 2: replaces the old `activeCases` lump.
  onHoldCases: number;
  openCases: number;
  retentionRate: number;
  settledCases: number;
  totalCases: number;
}

/**
 * Wrapper response: per-campaign rows plus firm-wide KPIs, Acquisition Volume,
 * and Top Growth. Powers the firm details page.
 */
export interface FirmCampaignStatisticsResponse {
  acquisitionData: AcquisitionData;
  campaigns: FirmCampaignStatistics[];
  kpiStats: FirmKpiStats;
  topGrowth: FirmTopGrowthItem[];
}

// ========================================
// API METHODS
// ========================================

const organizationsApi = {
  /**
   * Get firm statistics for all firms accessible to the current user
   * Returns array of firms with campaign counts, case counts, etc.
   */
  getFirmStatistics: async (): Promise<FirmStatisticsResponse[]> => {
    const response = await apiClient.get<FirmStatisticsResponse[]>(
      "/organizations/firm-statistics"
    );
    return response.data;
  },

  /**
   * Get campaign-specific statistics for a firm. Returns the firm-wide rollup
   * (KPI tiles, Acquisition Volume, Top Growth) plus per-campaign rows that
   * power the campaigns table on the firm details page.
   */
  getFirmCampaignStatistics: async (firmId: string): Promise<FirmCampaignStatisticsResponse> => {
    const response = await apiClient.get<FirmCampaignStatisticsResponse>(
      `/organizations/${firmId}/campaign-statistics`
    );
    return response.data;
  },

  /**
   * List users granted access by the caller to view a specific firm's data.
   * Backs FirmDetails (`/firms/:firmId`) and OrgUsersModal. Backend gates with
   * EXTERNAL_REPORTS plus a per-request shared-campaign ownership check.
   */
  listUsers: async (orgId: string): Promise<OrganizationUsersResponse> => {
    const response = await apiClient.get<OrganizationUsersResponse>(
      `/admin/organizations/${orgId}/users`
    );
    return response.data;
  },

  /**
   * List all referring organizations visible to the current handling org
   */
  externalOrganizations: async (): Promise<
    {
      campaignCount: number;
      caseCount: number;
      id: string;
      name: string;
      orgType: OrgType;
      parentId?: string;
      role?: "alias" | "canonical" | "root_identity";
      userCount: number;
    }[]
  > => {
    const response = await apiClient.get("/organizations/created-orgs");
    return response.data;
  },
};

export default organizationsApi;
